/**
 * Map Layer Domain Types
 *
 * Frontend domain types for custom map layers (GeoJSON, KML, GPX).
 * Mapped from API DTOs by layerMapper.
 */

/** Standard (built-in) layer visibility flags */
export interface LayerVisibility {
  polandBorder: boolean;
  voivodeshipBorders: boolean;
  nationalRoads: boolean;
  voivodeshipRoads: boolean;
}

/**
 * Default visibility for standard layers. Derived from the single-source
 * registry in `config/mapFeatures.ts` and re-exported here so existing
 * `types/layers` import paths keep working.
 */
export { DEFAULT_VISIBILITY } from '../../config/mapFeatures';

/** Artifact layer visibility flags (tasks, notes, obstructions on map) */
export interface ArtifactVisibility {
  tasks: boolean;
  notes: boolean;
  obstructions: boolean;
}

/** Default visibility for artifact layers */
export const DEFAULT_ARTIFACT_VISIBILITY: ArtifactVisibility = {
  tasks: true,
  notes: true,
  obstructions: true,
};

/** Supported source formats for uploaded layers */
export type MapLayerFormat =
  | 'GeoJson'
  | 'Kml'
  | 'Kmz'
  | 'Gpx'
  | 'GeoPackage'
  | 'Shapefile';

/** Style configuration for rendering a custom layer on the map */
export interface LayerStyle {
  /** Fill/line color (hex string) */
  color?: string;
  /** Line/stroke opacity (0-1) */
  opacity?: number;
  /** Line width in pixels */
  lineWidth?: number;
  /** Fill opacity for polygons (0-1) */
  fillOpacity?: number;
}

/** Frontend domain model for a custom map layer */
export interface MapLayer {
  id: string;
  name: string;
  description: string | null;
  sourceFormat: MapLayerFormat;
  fileSizeBytes: number;
  featureCount: number;
  boundsJson: string | null;
  sortOrder: number;
  isVisible: boolean;
  styleJson: string | null;
  uploadedBy: string;
  uploaderName: string;
  createdAt: Date;
  updatedAt: Date | null;
}
